import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

type Tab = 'documents' | 'websites' | 'search';

interface Source {
  id: string;
  type: 'document' | 'website' | 'search';
  title: string | null;
  url: string | null;
  status: 'pending' | 'processing' | 'ready' | 'error';
  chunksCount: number;
  pagesCrawled: number;
  errorMessage: string | null;
  createdAt: string;
}

const STATUS_BADGE: Record<Source['status'], string> = {
  pending:    'badge-slate',
  processing: 'badge-amber',
  ready:      'badge-green',
  error:      'badge-red',
};

export default function KnowledgeBase() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('documents');
  const [sources, setSources] = useState<Source[]>([]);
  const [url, setUrl] = useState('');
  const [crawlMode, setCrawlMode] = useState<'single_page' | 'full_crawl'>('single_page');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = () => api.get('/knowledge/sources').then((r) => setSources(r.data.sources)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [user, navigate]);

  async function uploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/knowledge/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (fileRef.current) fileRef.current.value = '';
      toast.success(`Загружено: ${file.name}`);
      load();
    } finally { setBusy(false); }
  }
  async function submitUrl() {
    if (!url) return;
    setBusy(true);
    try { await api.post('/knowledge/add-url', { url, crawlMode }); setUrl(''); toast.info('Парсинг запущен'); load(); }
    finally { setBusy(false); }
  }
  async function submitSearch() {
    if (!query) return;
    setBusy(true);
    try { await api.post('/knowledge/search-web', { query }); setQuery(''); toast.info('Поиск запущен'); load(); }
    finally { setBusy(false); }
  }
  async function removeSource(id: string) {
    if (!confirm('Удалить источник и все его чанки?')) return;
    await api.delete(`/knowledge/sources/${id}`);
    load();
  }

  const filtered = sources.filter((s) =>
    tab === 'documents' ? s.type === 'document' :
    tab === 'websites'  ? s.type === 'website'  : s.type === 'search'
  );

  const TABS = [
    { key: 'documents', label: '📄 Документы',  hint: 'PDF, DOCX, TXT, MD' },
    { key: 'websites',  label: '🌐 Сайты',       hint: 'URL или весь сайт' },
    { key: 'search',    label: '🔎 Веб-поиск',   hint: 'Brave + синтез AI' },
  ] as const;

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">База знаний</h1>
          <p className="text-slate-500 mt-1.5">Факты, которые AI подтянет в твои скрипты. Загрузи доки или ссылки.</p>
        </header>

        <div className="inline-flex card-glass p-1 mb-6 gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                tab === t.key
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-white/60'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'documents' && (
          <div className="card-glass p-6 mb-6">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-300 hover:bg-indigo-50/30 transition">
              <div className="text-4xl mb-2">📂</div>
              <p className="text-sm font-medium mb-1">Перетащи или выбери файл</p>
              <p className="text-xs text-slate-500 mb-1">PDF · DOCX · TXT · MD · MP3 · M4A · WAV · WEBM · до 50 МБ</p>
              <p className="text-[11px] text-slate-400 mb-4">
                🎙 Аудио из <b>NotebookLM Audio Overview</b> или любая запись —
                автотранскрипт через OpenAI Whisper.
              </p>
              <input ref={fileRef} type="file"
                accept=".pdf,.docx,.txt,.md,.mp3,.m4a,.wav,.webm,.ogg,.mp4,audio/*"
                onChange={uploadFile} disabled={busy}
                className="block mx-auto text-sm" />
            </div>
          </div>
        )}

        {tab === 'websites' && (
          <div className="card-glass p-6 mb-6 space-y-3">
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/article"
              className="input"/>
            <div className="flex flex-wrap gap-3 items-center">
              <select value={crawlMode} onChange={(e) => setCrawlMode(e.target.value as 'single_page' | 'full_crawl')} className="select">
                <option value="single_page">Одна страница</option>
                <option value="full_crawl">Весь сайт (до 100 стр, глубина 3)</option>
              </select>
              <button onClick={submitUrl} disabled={busy || !url} className="btn btn-md btn-primary">
                🌐 Добавить
              </button>
            </div>
          </div>
        )}

        {tab === 'search' && (
          <div className="card-glass p-6 mb-6 space-y-3">
            <p className="text-sm text-slate-600">
              Brave ищет топ-10 результатов → парсится топ-5 → AI синтезирует единый документ.
            </p>
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Например: автоматизация малого бизнеса"
              className="input"/>
            <button onClick={submitSearch} disabled={busy || !query} className="btn btn-md btn-primary">
              🔎 Запустить поиск
            </button>
          </div>
        )}

        <section>
          <h2 className="text-lg font-semibold mb-3 text-slate-700">
            Источники <span className="text-slate-400 font-normal">· {filtered.length}</span>
          </h2>
          {filtered.length === 0 ? (
            <div className="card-glass p-12 text-center">
              <div className="text-5xl mb-3">📚</div>
              <p className="text-sm text-slate-500">Пусто. Добавь источник из вкладки выше.</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {filtered.map((s) => (
                <div key={s.id} className="card-glass p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{s.title ?? '—'}</div>
                    {s.url && <div className="text-xs text-slate-500 truncate">{s.url}</div>}
                    {s.errorMessage && <div className="text-xs text-rose-600 mt-1">{s.errorMessage}</div>}
                  </div>
                  <div className="text-right">
                    <div className={`badge ${STATUS_BADGE[s.status]}`}>{s.status}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.chunksCount} чанков</div>
                  </div>
                  <button onClick={() => removeSource(s.id)} className="text-slate-400 hover:text-rose-600">🗑</button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
